import type { Map } from "../map";
import type LayerViewModel from "../map/LayerViewModel";
import type { ApiOpts } from "../model/Models";

const es = global.es; // TODO Remove dependency

const salesforceDefaultObjects = [
    {'objectType' : 'Lead', 'active' : true, 'fields' : [], 'label' : 'Leads', 'recordTypeInfos': [], 'joinObjects':{}},
    {'objectType' : 'Account', 'active' : false, 'fields' : [], 'label' : 'Accounts', 'recordTypeInfos': [], 'joinObjects':{}},
    {'objectType' : 'Opportunity', 'active' : false, 'fields' : [], 'label' : 'Opportunities', 'recordTypeInfos': [], 'joinObjects':{}},
    {'objectType' : 'Contact', 'active' : false, 'fields' : [], 'label' : 'Contacts', 'recordTypeInfos': [], 'joinObjects':{}}
];

/**
 * Convenience methods to simplify conditional logic
 */
export default class SalesforceUtil {
    customObjectsCache; // cache property for the custom Objects
    SFObjectDescriptionCache; // cache property for each object type (already with relationship dependencies)

    constructor() {
        this.customObjectsCache = null;
        this.SFObjectDescriptionCache = {};
    }

    /**
     * 
     * @param obj 
     * 
     * @returns 
     */
    isSfImportMeta(obj): boolean {
        if (!obj) {
            return false;
        }

        if (obj?.importMeta) {
            return this.isSfImportMeta(obj.importMeta);
        }

        return (obj.$type === "SalesforceObjectImportMeta" || obj.$type === "SalesforceReportImportMeta");
    }

    /**
     * 
     * @param obj 
     * 
     * @returns 
     */
    isReportImportMeta(obj): boolean {
        return (this.isSfImportMeta(obj) && obj.$type === "SalesforceReportImportMeta");
    }

    /**
     * 
     * @param obj 
     * 
     * @returns 
     */
    isObjectImportMeta(obj): boolean {
        return (this.isSfImportMeta(obj) && obj.$type === "SalesforceObjectImportMeta");
    }

    /**
     * Detect if layerView/layerMeta/importMeta is from another org (if so, we usually treat it as a non-sf layer)
     * NOTE: This method will throw an error if user is not embedded in sfdc (es.isEmbeddedInSFDC == false). 
     * 
     * @param obj 
     * 
     * @returns 
     */
    isMyOrgSfImportMeta(obj): boolean {
        if (!es.isEmbeddedInSFDC) {
            throw new Error("isMyOrgSfImportMeta cannot determine if org ownership match as es.isEmbeddedInSFDC is false");
        }

        if (obj?.importMeta) {
            return this.isSfImportMeta(obj.importMeta) && es.SFDC.signedRequest.context.organization.organizationId === obj.importMeta.orgId;
        }

        return this.isSfImportMeta(obj) && es.SFDC.signedRequest.context.organization.organizationId === obj.orgId;
    }

    /**
     * 
     * @param map 
     * 
     * @returns 
     */
    isMapContainSfImportMeta(map: Map): boolean {
        const layers = map.layer.list();
        for (const layer of layers) {
            if (this.isSfImportMeta(layer.getImportMetaObjRef())) {
                return true;
            }
        }

        return false;
    }

    /**
     * 
     * @param mappingAttribute 
     * @param customObject 
     * @param whereClauses 
     * 
     * @returns 
     */
    isUniqueMappingAttributeInSF(mappingAttribute: string, customObject: string, whereClauses?: string): JQueryDeferred<boolean> {
        const soql =
            "SELECT " + mappingAttribute + ", COUNT(Id)"
            + " FROM " + customObject
            + " WHERE " + mappingAttribute + " != null"
            + " AND " + mappingAttribute + " != ''"
            + (whereClauses ? " AND " + whereClauses : "")
            + " GROUP BY " + mappingAttribute + ""
            + " HAVING count(Id) > 1"
            + " LIMIT 1";
        
        return es.SFDC.client.executeQueryRequest(soql).then(result => {
            return (result?.done && result?.totalSize == 0);
        });
    }

    /**
     * 
     * @param sfoName 
     * @param isCustomObject 
     * @param opts 
     * 
     * @returns 
     */
    getSalesforceDataObject(sfoName: string, isCustomObject: boolean, opts?: ApiOpts): JQueryPromise<any> {
        if (this.SFObjectDescriptionCache?.[sfoName] !== undefined) {
            return es.util.deferred.resolved(this.SFObjectDescriptionCache[sfoName]);
        }

        const salesforceObjects = salesforceDefaultObjects;

        const relationships = [];
        return es.SFDC.client.describe(sfoName, undefined, opts).then(function(rs){
            const dfds = [es.util.deferred.resolved(rs)];
            // loop through the fields to discover relationships
            let blackList = es.salesforceSettings["sfBlacklistJoinRelations"];
            if (!blackList){
                blackList = [];
            }
            for (const field of rs.fields) {
                if (field.relationshipName != null && !blackList.includes(field.relationshipName)) {
                    relationships.push({
                        relationshipName: field.relationshipName,
                        relationshipLabel: field.label
                    });
                    if (field.relationshipName === 'Owner' || field.referenceTo[0] === "User"){
                        // FIXME :: how do we test looking up groups?
                        // Whe need to describe the User table to get all the field definitions in order to find the nameField
                        const userFields = es.salesforceSettings["sfWhitelistSelectFlds_User"];
                        const fields = userFields.map(fieldName =>({
                            "name" : fieldName,
                            "label" : fieldName,
                            "type" : "string",
                            "filterable" : true,
                            "updateable" : false,
                        }))
                        dfds.push(es.util.deferred.resolved({'name': 'User', 'fields':fields}));
                    } else {
                        dfds.push(es.SFDC.client.describe(field.referenceTo[0]));
                    }
                }
            }
            return Promise.all(dfds);
        }).then((args) => {
            const getFields = (sfObj) => {
                return sfObj.fields.map((field) => {
                    // Beautify the label
                    if (field.type === "reference" && field.label.toLowerCase().endsWith(" id")) {
                        field.label = field.label.slice(0, -3);
                    }

                    return this.reduceField(field);
                });
            };

            const getRecordTypeInfos = (sfObj) => {
                return sfObj.recordTypeInfos.filter((recordTypeInfo) => {
                    return recordTypeInfo.active && recordTypeInfo.available && recordTypeInfo.master == false;
                }).map((recordTypeInfo) => {
                    return {
                        'name': recordTypeInfo.name,
                        'recordTypeId': recordTypeInfo.recordTypeId
                    };
                });
            };

            const getJoinObject = (sfObj, relationshipLabel) => {
                const joinObj = {'objectType': sfObj.name, 'fields':[]};
                // Beautify the label
                joinObj.label = relationshipLabel.toLowerCase().endsWith(" id") ? relationshipLabel.slice(0,-3) : relationshipLabel;
                joinObj.fields = sfObj.fields.map((field) => {
                    return this.reduceField(field);
                });

                return joinObj;
            };

            const sfObject = isCustomObject ? this.customObjectsCache[sfoName] : salesforceObjects.find(sfo => sfo.objectType == sfoName);
            sfObject.fields = getFields(args[0]);
            sfObject.joinObjects = {};

            for (let i=1; i<args.length; i++){
                const {relationshipName, relationshipLabel} = relationships[i-1];
                sfObject.joinObjects[relationshipName] = getJoinObject(args[i], relationshipLabel);
            }

            sfObject.supportedScopes = args[0].supportedScopes;

            const picklistValuesByRecordTypeDfds = [];
            if (!isCustomObject){
                sfObject.recordTypeInfos = getRecordTypeInfos(args[0]);
                sfObject.recordTypeInfos.forEach(recordType => {
                    picklistValuesByRecordTypeDfds.push(es.SFDC.client.getPicklistOptions(sfObject.objectType, recordType.recordTypeId));
                })
            }
            // Get the picklist values base on the Record Type
            return Promise.all(picklistValuesByRecordTypeDfds).then((rs) => {
                try {
                    for(let i = 0; i < rs.length; i++){
                        const response = rs[i];
                        for(let [fName, entry] of Object.entries(response.picklistFieldValues)){
                            const field = _.find(sfObject.fields, f => f.name === fName);
                            field.picklistValues[sfObject.recordTypeInfos[i].recordTypeId] = entry.values.map(val => ({label: val.label, value: val.value}));
                            if(field.nillable){
                                field.picklistValues[sfObject.recordTypeInfos[i].recordTypeId].unshift({label: "No Value", value: null});
                            }
                        }
                    }
                } catch (e){
                    console.warn("Error parsing picklist values for Record Type. No Restrictions will be applied", e);
                }

                this.SFObjectDescriptionCache[sfoName] = es.util.object.deepCopy(sfObject);
                return sfObject;
            }).catch(() => {
                // If the request for getting the picklist values fails, ignore.
                // All picklists will have all of the options
                console.warn("Fail to fetch Picklist values based on the Record Type");
                this.SFObjectDescriptionCache[sfoName] = es.util.object.deepCopy(sfObject);
                return sfObject;
            });
        });
    }

    /**
     * 
     * @param field 
     * 
     * @returns 
     */
    private reduceField(field) {
        // Check if picklist values can be null
        let picklistValues = null;
        if (field.picklistValues) {
            picklistValues = field.picklistValues;
            if (field.nillable) {
                picklistValues.unshift({label: "No value", value: null});
            }
        }

        return {
            'name': field.name,
            'label': field.label,
            'length': field.length ?? 0,
            'type': field.type,
            'picklistValues': {default: picklistValues},
            'relationshipName': field.relationshipName,
            'referenceTo': field.referenceTo,
            referenceTargetField: field.referenceTargetField,
            nameField: field.nameField,
            'soapType': field.soapType,
            'filterable': field.filterable,
            custom: field.custom,
            groupable: field.groupable,
            nillable: field.nillable,
            compoundFieldName: field.compoundFieldName,
            updateable: field.updateable
        };
    };

    /**
     * 
     * @returns 
     */
    getCustomObjects(): JQueryPromise<any> {
        if (this.customObjectsCache !== null) {
            return es.util.deferred.resolved(this.customObjectsCache);
        }
        return es.SFDC.client.describeCustom({noCache: true}).then((customObjs) => {
            //es.log.log("getCustomObjects NOCACHE", customObjs);
            const salesforceCustomObjects = {};
            for (const customObj of customObjs) {
                salesforceCustomObjects[customObj.name] = {
                    'objectType' : customObj.name,
                    'active' : false,
                    'label' : customObj.label,
                    'labelPlural' : customObj.labelPlural,
                    'fields' : null
                };
            }
            this.customObjectsCache = es.util.object.deepCopy(salesforceCustomObjects);
            return salesforceCustomObjects;
        }).fail(function(rs){
            es.error.handleUIError("Error fetching custom objects from salesforce");
            console.error("Error fetching custom objects from salesforce", rs);
        });
    }

    /**
     * 
     * @param layer 
     * @param attributes 
     * @param feature 
     * 
     * @returns 
     */
    mergeSalesforceAttributeTypes(layer: LayerViewModel, attributes: any[], feature): JQueryDeferred<any[]> {
        return this.getCustomObjects().then((_customObjects) => {
            const importMeta = layer.getImportMetaObjRef();
            const isCustomSalesforceObject = importMeta.salesforceObjectType === "Custom";
            const objectName =  isCustomSalesforceObject ? importMeta.objectName : importMeta.salesforceObjectType;
            return this.getSalesforceDataObject(objectName, isCustomSalesforceObject).then(SFObjectDescription => {
                let recordTypeIdDfd;
                if(feature && SFObjectDescription.recordTypeInfos?.length > 0) {
                recordTypeIdDfd = es.SFDC.client.getRecordTypeId(objectName, feature.attrs["ID"]);
                } else {
                    recordTypeIdDfd = es.util.deferred.resolved(null);
                }

                return recordTypeIdDfd.then((recordTypeId) => {
                    attributes = this.getAttributesWithMetadata(layer);
                    attributes = attributes.map(attr => {
                        const describeField = attr.$type === "ReferencedFieldMetadata" ?
                            _.find(SFObjectDescription.joinObjects[attr.relationship].fields, f => f.name === attr.targetField) :
                            _.find(SFObjectDescription.fields, f => f.name === attr.soqlField);

                        // If the describe field is undefined it means that the user does not have access to this field (it is not available in the object describe)
                        // This should only happen when viewing a shared dataset
                        if (!describeField) {
                            attr.editable = false;
                            return attr;
                        }

                        //Check if the attribute is Editable
                        const isAttributeEditable = () => {
                            const NON_EDITABLE_FIELD_TYPES = ["phone", "email", "address", "id", "percent", "date", "datetime", "time"];
                            const NON_EDITABLE_COMPOUND_FIELD_TYPES = ["address", "name", "shippingaddress", "billingaddress"]; // should be lowercase
                            const NON_EDITABLE_RELATIONSHIPS = ["RecordType"];
                            return describeField.updateable
                                && (describeField.compoundFieldName === null || !NON_EDITABLE_COMPOUND_FIELD_TYPES.includes(describeField.compoundFieldName.toLowerCase()))
                                && !NON_EDITABLE_FIELD_TYPES.includes(describeField.type)
                                && attr.$type !== "ReferencedFieldMetadata"
                                && (attr.relationship === null || !NON_EDITABLE_RELATIONSHIPS.includes(attr.relationship));
                        }
                        attr.editable = isAttributeEditable();

                        // Find the label field for Lookup Fields
                        if (attr.$type === "LookupFieldMetadata") {
                            attr.nameField = _.find(SFObjectDescription.joinObjects[attr.relationship]?.fields, f => f.nameField)?.name ?? "Name";
                            return attr;
                        }

                        // Set the options for picklist values
                        if (describeField?.type === "picklist") {
                            attr.attrType = "PICKLIST";
                            attr.options = describeField.picklistValues[recordTypeId] ?? describeField.picklistValues["default"];
                        }

                        // Set the options for multi-picklist values
                        if (describeField?.type === "multipicklist") {
                            attr.attrType = "MULTIPICKLIST";
                            attr.options = describeField.picklistValues[recordTypeId] ?? describeField.picklistValues["default"];
                        }

                        return attr;
                    });

                    return attributes;
                });
            });
        });
    }

    /**
     * 
     * @param layer 
     * 
     * @returns 
     */
    getAttributesWithMetadata(layer: LayerViewModel) {
        const attributes = es.util.object.deepCopy(layer.getLayerInfo().attributes);
        const importMeta = layer.getImportMetaObjRef();
        if (importMeta !== null) {
            return attributes.map(attr => ({...attr, ...(importMeta.attributesMetadata[attr.name])}));
        }
        return attributes;
    }

    /**
     * 
     * @param sfObj 
     * @param scope 
     * @param whereClause 
     * 
     * @returns 
     */
    runCountQuery(sfObj, scope: string, whereClause?: string): JQueryDeferred<number> {
        let query = "SELECT COUNT()";
        query += " FROM " + sfObj.objectType;
        query += this.getUsingClause(scope);
        if (whereClause) {
            query += ' ' + whereClause.trim();
        }

        return es.SFDC.client.executeQueryRequest(query, "countQuery__" + query).then((payload) => {
            return (payload.totalSize ? payload.totalSize : 0);
        });
    }

    /**
     * 
     * @param scope 
     * 
     * @returns 
     */
    getUsingClause(scope: string): string {
        if (scope == 'everything') {
            return '';
        }

        //console.log("getUsingClause", scope);
        if (scope === null || scope === undefined) {
            if (es.isDeveloper) {
                console.trace("getUsingClause : invalid scope param [" + scope + "]");
            }
            // default to 'mine as that is the default in the select lists
            scope = 'mine';
        }

        return ' USING SCOPE ' + scope;
    }
}
